import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router";
import { planningFlags } from "@shared/planning.ts";
import { stayForDate } from "@shared/itinerary.ts";
import { allergyPhrasesForTrip, dietaryPhrasesForTrip, phraseTitle, usefulPhrasesForTrip } from "@shared/phrases.ts";
import type { ThaiPhrase } from "@shared/schema.ts";
import { formatLongDate, todayInZone, tripPhase } from "@shared/time.ts";
import { useSession } from "../../app/session.tsx";
import { useTrip, useTripClock } from "../../app/trip.tsx";
import { fetchExport, fetchLive, lookupHotelLocations } from "../../lib/api.ts";
import { downloadPack, formatBytes, packAgeLabel, readPackMeta, type PackMeta, type PackProgress } from "../../lib/offline.ts";
import { fxRate, type LivePayload } from "../../lib/live.ts";
import { allergyLines } from "../../lib/records.ts";
import { copyText, safeHttpUrl } from "../../lib/format.ts";
import { humanStatus } from "@shared/labels.ts";
import { createId } from "@shared/ids.ts";
import { PhraseDeck } from "../../components/PhraseDeck.tsx";
import { AllergyCard } from "../../components/AllergyCard.tsx";
import { PhraseOwnerTools } from "../../components/PhraseOwnerTools.tsx";
import { AlertSetupPanel } from "../updates/AlertSetup.tsx";
import { fetchPushStatus, reportPackRevision } from "../../lib/push.ts";

function emergencyRank(role: string): number {
  const value = role.toLowerCase();
  if (/(insur|policy)/.test(value)) return 0;
  if (/emergency|assistance|embassy/.test(value)) return 1;
  if (/hotel|concierge/.test(value)) return 2;
  if (/family|dad|adele|parent/.test(value)) return 3;
  if (/driver|transfer|kasem/.test(value)) return 4;
  return 10;
}

export function EssentialsPage() {
  const { trip, refresh, lastUpdatedAt, syncState } = useTrip();
  const { role, offlineReader } = useSession();
  const pack = readPackMeta();
  const now = useTripClock();
  const phase = trip
    ? tripPhase(now, trip.trip.startDate, trip.trip.endDate, trip.trip.homeTimezone)
    : "before";
  const date = trip ? todayInZone(now, phase === "before" ? trip.trip.homeTimezone : trip.trip.diaryTimezone) : "";
  const stay = trip ? stayForDate(trip, date) ?? trip.stays[0] : undefined;
  const hotel = stay && trip ? trip.places.find((p) => p.id === stay.placeId) : undefined;
  const milk = trip ? allergyPhrasesForTrip(trip.thaiPhrases)[0] : undefined;
  const flags = trip ? planningFlags(trip) : [];

  if (!trip) return <main className="page">Loading essentials…</main>;

  return (
    <main className="page essentials-hub">
      <h1 className="serif">Essentials</h1>
      <p className="lead">The few things we may need without a signal: allergy card, hotel, pickup, and this phone.</p>

      {milk ? (
        <Link className="hub-feature" to={`/show/${milk.id}`}>
          <p className="kicker">For restaurant staff</p>
          <h2 className="serif">{phraseTitle(milk)}</h2>
          <p lang="th" className="hub-thai">{milk.thai}</p>
          <p>{milk.translationReviewed ? "Show this card full screen" : "Phrase aid. Thai not reviewed yet. Show this card full screen."}</p>
        </Link>
      ) : null}

      <div className="hub-grid">
        <Link className="hub-tile" to={hotel ? `/show/driver/${hotel.id}` : "/essentials/emergency"}>
          <h2 className="serif">Driver card</h2>
          <p>{hotel?.name ?? "Hotel address when we have one"}</p>
        </Link>
        <Link className="hub-tile" to="/essentials/emergency">
          <h2 className="serif">Emergency</h2>
          <p>Hotel, insurance, family contacts, print sheet</p>
        </Link>
        <Link className="hub-tile" to="/essentials/cards">
          <h2 className="serif">Thai cards</h2>
          <p>Allergy, dietary and useful phrases</p>
        </Link>
        <Link className="hub-tile" to="/essentials/phone">
          <h2 className="serif">This phone</h2>
          <p>{pack.ready ? packAgeLabel(pack) : "Download the trip for airplane mode"}</p>
        </Link>
        <Link className="hub-tile" to="/essentials/money">
          <h2 className="serif">Pocket converter</h2>
          <p>Indicative GBP and THB only</p>
        </Link>
        <Link className="hub-tile" to="/essentials/review">
          <h2 className="serif">Still to confirm</h2>
          <p>{flags.length ? `${flags.length} open items` : "Planning list"}</p>
        </Link>
      </div>

      <p className="muted">
        {syncState === "offline" ? "Offline pack." : syncState === "session_expired" ? "Sign in again to edit." : lastUpdatedAt ? `Updated ${lastUpdatedAt.slice(11, 16)}` : "Shared trip"}
        {offlineReader ? " Read-only on this device." : ""}
        {role === "owner" ? " Owner can edit." : ""}
      </p>
      <button className="btn-ghost" type="button" onClick={() => void refresh()}>Refresh</button>
    </main>
  );
}

export function CardsPage() {
  const { trip, savePhraseWrite } = useTrip();
  const { role } = useSession();
  const [phraseId, setPhraseId] = useState<string | null>(null);
  const [dietaryId, setDietaryId] = useState<string | null>(null);
  if (!trip) return <main className="page">Loading cards…</main>;
  const phrases = usefulPhrasesForTrip(trip.thaiPhrases);
  const dietary = dietaryPhrasesForTrip(trip.thaiPhrases);
  const phraseIndex = Math.max(0, phrases.findIndex((p) => p.id === phraseId));
  const allergyIndex = dietaryId === "__notes__"
    ? dietary.length
    : dietaryId
      ? Math.max(0, dietary.findIndex((p) => p.id === dietaryId))
      : 0;
  const visibleDietary = allergyIndex < dietary.length ? dietary[allergyIndex] : null;
  const visiblePhrase = phrases[phraseIndex] ?? null;
  const unreviewed = [...dietary, ...phrases].filter((row) => !row.translationReviewed);

  function focusSaved(next: ThaiPhrase[], focusId?: string) {
    if (!focusId) return;
    if (usefulPhrasesForTrip(next).some((p) => p.id === focusId)) setPhraseId(focusId);
    if (dietaryPhrasesForTrip(next).some((p) => p.id === focusId)) setDietaryId(focusId);
  }

  return (
    <main className="page">
      <Link className="btn-ghost" to="/essentials">Back</Link>
      <h1 className="serif">Thai cards</h1>
      <p className="muted">Show a card full screen so staff can read the Thai. Check each unreviewed card on its own: English, Thai, then what that Thai reads as.</p>
      {role === "owner" && unreviewed.length ? (
        <p className="banner warn">
          {unreviewed.length === 1 ? "1 card still needs a wording check." : `${unreviewed.length} cards still need a wording check.`}
          {" "}
          <button
            className="btn-ghost"
            type="button"
            onClick={() => {
              const first = dietary.find((row) => !row.translationReviewed) ?? phrases.find((row) => !row.translationReviewed);
              if (!first) return;
              if (dietary.some((row) => row.id === first.id)) setDietaryId(first.id);
              else setPhraseId(first.id);
              const section = dietary.some((row) => row.id === first.id) ? "dietary" : "useful";
              requestAnimationFrame(() => {
                document.querySelector(`[data-phrase-review="${section}"]`)?.scrollIntoView({ block: "center" });
              });
            }}
          >
            Check the card on the deck
          </button>
        </p>
      ) : null}
      <section>
        <h2 className="serif">Allergy and dietary</h2>
        {role === "owner" ? (
          <PhraseOwnerTools
            tripPhrases={trip.thaiPhrases}
            savePhraseWrite={savePhraseWrite}
            kind={visibleDietary?.kind === "allergy" ? "allergy" : "dietary"}
            visible={visibleDietary}
            section="dietary"
            onSaved={focusSaved}
            deck={
              <AllergyCard
                trip={trip}
                index={allergyIndex}
                onIndexChange={(next) => {
                  const card = dietary[next];
                  setDietaryId(card?.id ?? "__notes__");
                }}
              />
            }
          />
        ) : (
          <AllergyCard
            trip={trip}
            index={allergyIndex}
            onIndexChange={(next) => {
              const card = dietary[next];
              setDietaryId(card?.id ?? "__notes__");
            }}
          />
        )}
      </section>
      <section>
        <h2 className="serif">Useful Thai</h2>
        {role === "owner" ? (
          <PhraseOwnerTools
            tripPhrases={trip.thaiPhrases}
            savePhraseWrite={savePhraseWrite}
            kind="phrase"
            visible={visiblePhrase}
            section="useful"
            onSaved={focusSaved}
            deck={
              <PhraseDeck
                phrases={phrases}
                index={phraseIndex}
                onIndexChange={(next) => setPhraseId(phrases[next]?.id ?? null)}
              />
            }
          />
        ) : (
          <PhraseDeck
            phrases={phrases}
            index={phraseIndex}
            onIndexChange={(next) => setPhraseId(phrases[next]?.id ?? null)}
          />
        )}
      </section>
    </main>
  );
}

export function EmergencyPage() {
  const { trip, save } = useTrip();
  const { role } = useSession();
  const [insurance, setInsurance] = useState(trip?.trip.insuranceNote ?? "");
  const [contactName, setContactName] = useState("");
  const [contactRole, setContactRole] = useState("");
  const [contactPhone, setContactPhone] = useState("");
  const [copyState, setCopyState] = useState<string | null>(null);
  const [saveNote, setSaveNote] = useState<string | null>(null);
  const now = useTripClock();
  useEffect(() => {
    setInsurance(trip?.trip.insuranceNote ?? "");
  }, [trip?.trip.insuranceNote]);
  if (!trip) return <main className="page">Loading…</main>;
  const phase = tripPhase(now, trip.trip.startDate, trip.trip.endDate, trip.trip.homeTimezone);
  const date = todayInZone(now, phase === "before" ? trip.trip.homeTimezone : trip.trip.diaryTimezone);
  const stay = stayForDate(trip, date) ?? trip.stays.find((s) => s.startDate >= date);
  const hotel = stay ? trip.places.find((p) => p.id === stay.placeId) : undefined;
  const milk = allergyPhrasesForTrip(trip.thaiPhrases)[0];
  const emergencyContacts = [...trip.contacts].sort((a, b) => emergencyRank(a.role) - emergencyRank(b.role));
  const gaps: string[] = [];
  if (!hotel?.address) gaps.push("Current hotel address");
  if (!trip.trip.insuranceNote) gaps.push("Insurer and policy");
  if (!trip.contacts.some((c) => /family|dad|adele|parent/i.test(c.role) && c.phone)) gaps.push("A family contact with a phone number");
  if (!trip.contacts.some((c) => /emergency|assistance|embassy/i.test(c.role) && (c.phone || c.url))) gaps.push("Verified assistance contact");
  if (milk && !milk.translationReviewed) gaps.push("Independently reviewed milk-allergy wording");

  return (
    <main className="page">
      <Link className="btn-ghost" to="/essentials">Back</Link>
      <h1 className="serif">Emergency</h1>
      {milk ? <Link className="btn" to={`/show/${milk.id}`}>Milk allergy card</Link> : null}
      {role === "owner" && gaps.length ? (
        <section className="banner warn">
          <p>Incomplete before family release:</p>
          <ul className="plain-list">{gaps.map((gap) => <li key={gap}>{gap}</li>)}</ul>
        </section>
      ) : null}
      <section className="card">
        <h2 className="serif">Current hotel</h2>
        {hotel ? (
          <>
            <p>{hotel.name}</p>
            <p>{hotel.address ?? "Address not on the booking yet."}</p>
            <Link className="btn-secondary" to={`/show/driver/${hotel.id}`}>Show the driver</Link>
          </>
        ) : (
          <p className="muted">No hotel on the current day. After the trip we do not keep a first-hotel fallback here.</p>
        )}
      </section>
      <section className="card" id="insurance">
        <h2 className="serif">Insurance</h2>
        {trip.trip.insuranceNote ? <p>{trip.trip.insuranceNote}</p> : <p>Insurance is not in the trip yet. This is not cover.</p>}
        {role === "owner" ? (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              setSaveNote(null);
              void save({ ...trip, trip: { ...trip.trip, insuranceNote: insurance || null } }).then((result) => {
                setSaveNote(result.ok ? "Insurance note saved." : result.message);
              });
            }}
          >
            <label className="field">Note
              <textarea value={insurance} onChange={(e) => setInsurance(e.target.value)} rows={3} />
            </label>
            <button className="btn-secondary" type="submit">Save insurance note</button>
            {saveNote ? <p>{saveNote}</p> : null}
          </form>
        ) : null}
      </section>
      <section className="card">
        <h2 className="serif">Contacts</h2>
        {emergencyContacts.length === 0 ? <p>Contact details to add. No numbers were invented from screenshots.</p> : null}
        {emergencyContacts.map((c) => {
          const href = safeHttpUrl(c.url);
          return (
          <div key={c.id} className="contact-row" id={`contact-${c.id}`}>
            <div>
              <p>{c.name}</p>
              <p className="muted">{c.role}</p>
            </div>
            {c.phone ? (
              <div className="row-actions">
                <a className="btn" href={`tel:${c.phone}`}>Call</a>
                <button className="btn-ghost" type="button" onClick={() => void copyText(c.phone ?? "").then(setCopyState)}>Copy</button>
                {role === "owner" ? (
                  <button
                    className="btn-ghost"
                    type="button"
                    onClick={() => void save({ ...trip, contacts: trip.contacts.filter((row) => row.id !== c.id) })}
                  >
                    Remove
                  </button>
                ) : null}
              </div>
            ) : href ? (
              <a className="btn-secondary" href={href} target="_blank" rel="noreferrer">Open link</a>
            ) : (
              <span className="muted">Contact details to add</span>
            )}
          </div>
          );
        })}
        {copyState === "copied" ? <p>Copied.</p> : copyState === "denied" ? <p>Clipboard blocked on this device.</p> : null}
        {role === "owner" ? (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (!contactName) return;
              void save({
                ...trip,
                contacts: [
                  ...trip.contacts,
                  { id: createId("contact"), name: contactName, role: contactRole || "Supplier", phone: contactPhone || null, verified: false, sourceIds: [] },
                ],
              }).then((result) => {
                if (result.ok) {
                  setContactName("");
                  setContactRole("");
                  setContactPhone("");
                  setSaveNote("Contact saved.");
                } else {
                  setSaveNote(result.message);
                }
              });
            }}
          >
            <h3 className="serif">Add a contact</h3>
            <label className="field">Name<input value={contactName} onChange={(e) => setContactName(e.target.value)} /></label>
            <label className="field">Role<input value={contactRole} onChange={(e) => setContactRole(e.target.value)} /></label>
            <label className="field">Phone<input value={contactPhone} onChange={(e) => setContactPhone(e.target.value)} /></label>
            <button className="btn-secondary" type="submit">Save contact</button>
          </form>
        ) : null}
      </section>
      <Link className="btn-secondary" to="/essentials/print">Printable summary</Link>
    </main>
  );
}

export function PhonePage() {
  const { trip, refresh } = useTrip();
  const { role, logout } = useSession();
  const location = useLocation();
  const [pack, setPack] = useState<PackMeta>(readPackMeta());
  const [progress, setProgress] = useState<PackProgress | null>(null);
  const [packError, setPackError] = useState<string | null>(null);
  const [live, setLive] = useState<LivePayload | null>(null);
  const [lookupNote, setLookupNote] = useState<string | null>(null);
  const [lookupBusy, setLookupBusy] = useState(false);
  const [pushStatus, setPushStatus] = useState<Record<string, unknown> | null>(null);
  useEffect(() => {
    void fetchLive().then((raw) => setLive(raw as LivePayload)).catch(() => setLive(null));
    if (role === "owner") void fetchPushStatus().then(setPushStatus).catch(() => setPushStatus(null));
  }, [role]);
  useEffect(() => {
    if (location.hash !== "#offline-pack") return;
    document.getElementById("offline-pack")?.scrollIntoView({ block: "start" });
  }, [location.hash]);
  if (!trip) return <main className="page">Loading…</main>;
  const packStale = pack.ready && pack.revision != null && pack.revision < trip.revision;
  const heartbeat = pushStatus?.heartbeat as { at?: string; ok?: boolean; tasksRun?: string[]; skippedPaidFlights?: boolean; nextDue?: Record<string, string> } | undefined;
  const jobs = pushStatus?.jobs as { queued?: number; failed?: number; accepted?: number } | undefined;
  const flightMonitor = typeof pushStatus?.flightMonitor === "object" && pushStatus.flightMonitor
    ? pushStatus.flightMonitor as {
      title?: string;
      detail?: string;
      remainingMilliUsd?: number;
      capMilliUsd?: number;
      nextDue?: string | null;
      sourceAge?: string | null;
      liveAlerts?: boolean;
      guarantee?: string;
    }
    : null;
  const deviceErrors = Array.isArray(pushStatus?.devices)
    ? (pushStatus.devices as Array<{ profileId?: string | null; lastPushError?: string | null }>).filter((row) => row.lastPushError)
    : [];

  return (
    <main className="page">
      <Link className="btn-ghost" to="/essentials">Back</Link>
      <h1 className="serif">This phone</h1>
      <p><Link className="btn-secondary" to="/updates">Updates inbox</Link></p>
      <AlertSetupPanel />
      <section className="card" id="offline-pack">
        <h2 className="serif">Offline pack</h2>
        <p>Stores the itinerary, contacts and attached documents on this trusted device. Map tiles are not downloaded. Browser storage can still be evicted.</p>
        <p className="muted">
          {pack.ready
            ? `Ready. ${packAgeLabel(pack)}. Revision ${pack.revision ?? trip.revision}. ${formatBytes(pack.bytes)}${pack.documentCount ? ` · ${pack.documentCount} documents` : ""}`
            : "Not downloaded on this device."}
        </p>
        {packStale ? <p className="banner warn">Offline copy needs updating. Live trip revision {trip.revision} is newer than this download.</p> : null}
        {pack.missingDocuments?.length ? <p className="muted">Missing from the last download: {pack.missingDocuments.join(", ")}.</p> : null}
        {progress ? (
          <>
            <p>{progress.step}</p>
            <div className="progress" aria-valuenow={progress.done} aria-valuemax={progress.total}>
              <span style={{ width: `${Math.round((progress.done / progress.total) * 100)}%` }} />
            </div>
          </>
        ) : null}
        {packError ? <p className="banner crit">{packError}</p> : null}
        <button
          className="btn"
          type="button"
          onClick={() => {
            setPackError(null);
            void downloadPack(setProgress)
              .then((meta) => {
                setPack(meta);
                setProgress(null);
                if (meta.revision != null) void reportPackRevision(meta.revision);
              })
              .catch((e) => { setPackError(e.message); setProgress(null); setPack(readPackMeta()); });
          }}
        >
          {pack.ready ? "Update offline copy" : "Download trip pack"}
        </button>
      </section>
      <section className="card">
        <h2 className="serif">Export</h2>
        <a className="btn-secondary" href="/api/calendar.ics">Calendar snapshot</a>
        <button
          className="btn-ghost"
          type="button"
          onClick={() => void fetchExport(true).then((data) => {
            const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = "thailand-together-backup.json";
            a.click();
          })}
        >
          Download backup
        </button>
      </section>
      {role === "owner" && live?.providers ? (
        <section className="card">
          <h2 className="serif">Live services</h2>
          {live.providers.map((p) => (
            <p key={p.label}>{p.label}: {humanStatus(p.status)}{p.detail ? ` · ${p.detail}` : ""}</p>
          ))}
          <button
            className="btn-secondary"
            type="button"
            disabled={lookupBusy}
            onClick={() => {
              setLookupBusy(true);
              setLookupNote(null);
              void lookupHotelLocations()
                .then(async (data) => {
                  const rows = (data.results as Array<{ name: string; status: string; detail: string }> | undefined) ?? [];
                  setLookupNote(rows.map((r) => `${r.name}: ${r.status}. ${r.detail}`).join(" ") || "No hotel rows.");
                  await refresh();
                })
                .catch((e: Error) => setLookupNote(e.message))
                .finally(() => setLookupBusy(false));
            }}
          >
            {lookupBusy ? "Looking up…" : "Look up hotel locations"}
          </button>
          {lookupNote ? <p>{lookupNote}</p> : null}
        </section>
      ) : null}
      {role === "owner" && pushStatus ? (
        <section className="card">
          <h2 className="serif">Alert pipeline</h2>
          <p>Last scheduler tick: {heartbeat?.at ?? "not yet"}. Next due check: {heartbeat?.nextDue?.scheduler ?? flightMonitor?.nextDue ?? "every five minutes"}. {heartbeat?.ok === false ? "The last tick reported a problem." : "A tick here is not proof every phone displayed a message or that every feed is healthy."}</p>
          <p className="muted">
            {flightMonitor
              ? `${flightMonitor.title ?? "Flight monitoring"}. ${flightMonitor.detail ?? ""} Family lock-screen flight alerts stay separate from lookups${flightMonitor.liveAlerts ? " and are currently enabled." : " and stay in review mode until PUSH_LIVE_FLIGHTS is on."}${typeof flightMonitor.capMilliUsd === "number" && flightMonitor.capMilliUsd > 0 && typeof flightMonitor.remainingMilliUsd === "number" ? ` Remaining app allowance this period: ${(flightMonitor.remainingMilliUsd / 1000).toFixed(2)} of ${(flightMonitor.capMilliUsd / 1000).toFixed(2)} USD. ${flightMonitor.guarantee ?? ""}` : ""}`
              : heartbeat?.skippedPaidFlights
                ? "Flight lookups are off. Free reminders still run."
                : "Flight lookups run near a booked window when the owner safety gate is open."}
            {" "}Jobs queued {jobs?.queued ?? 0}, accepted {jobs?.accepted ?? 0}, failed {jobs?.failed ?? 0}.
          </p>
          <p className="muted">Source age for the last tick: {heartbeat?.at ?? "none yet"}. Tasks: {(heartbeat?.tasksRun ?? []).join(", ") || "none"}. Official warnings and marine copy stay in review until those sources are signed off.</p>
          {deviceErrors.length ? (
            <p className="banner warn">
              A phone’s last push was rejected: {deviceErrors.map((row) => `${row.profileId ?? "unknown"}: ${row.lastPushError}`).join(" · ")}
            </p>
          ) : null}
          <p className="muted">Official warning ingestion stays unproven. Failure means unknown, never an all-clear. Optional email or SMS escalation is not configured.</p>
        </section>
      ) : null}
      <p className="muted">Weather by Open-Meteo. FX by Frankfurter. Maps by MapTiler/OSM when configured. Offline maps are not included in the trip pack.</p>
      <section className="card">
        <h2 className="serif">Log out</h2>
        <p>Logging out clears the downloaded trip from this browser and revokes this phone's alert registration. Ordinary session expiry is different: it does not remove a trusted device's notification permission by itself.</p>
        <button className="btn-ghost" type="button" onClick={() => void logout()}>Log out on this device</button>
      </section>
    </main>
  );
}

export function MoneyPage() {
  const { trip } = useTrip();
  const [live, setLive] = useState<LivePayload | null>(null);
  const [amount, setAmount] = useState("20");
  const [fromCurrency, setFromCurrency] = useState<"GBP" | "THB">("GBP");
  useEffect(() => {
    void fetchLive().then((raw) => setLive(raw as LivePayload)).catch(() => setLive(null));
  }, []);
  if (!trip) return <main className="page">Loading…</main>;
  const rate = fxRate(live?.fx);
  const thbPerGbp = rate?.thbPerGbp ?? 0;
  const converted =
    fromCurrency === "GBP" && thbPerGbp
      ? (Number(amount) * thbPerGbp).toFixed(0)
      : fromCurrency === "THB" && thbPerGbp
        ? (Number(amount) / thbPerGbp).toFixed(2)
        : "";
  const resultCurrency = fromCurrency === "GBP" ? "THB" : "GBP";
  return (
    <main className="page">
      <Link className="btn-ghost" to="/essentials">Back</Link>
      <h1 className="serif">Pocket converter</h1>
      <p className="muted">Bookings stay in the currency on the confirmation. This box never changes what anyone paid or owes.</p>
      <p className="muted">
        {rate ? `About 1 GBP ≈ ${rate.thbPerGbp} THB on ${rate.asOf}.` : "Indicative FX unavailable right now."}
      </p>
      <label className="field">Amount
        <input value={amount} onChange={(e) => setAmount(e.target.value)} inputMode="decimal" />
      </label>
      <div className="chip-row">
        <button className={`chip ${fromCurrency === "GBP" ? "selected" : ""}`} type="button" onClick={() => setFromCurrency("GBP")}>From GBP</button>
        <button className={`chip ${fromCurrency === "THB" ? "selected" : ""}`} type="button" onClick={() => setFromCurrency("THB")}>From THB</button>
      </div>
      {converted ? <p className="tabular" style={{ fontSize: "1.25rem" }}>{converted} {resultCurrency}</p> : null}
    </main>
  );
}

export function ReviewPage() {
  const { trip, save } = useTrip();
  const { role } = useSession();
  const [note, setNote] = useState<string | null>(null);
  return (
    <main className="page">
      <Link to="/essentials">Back</Link>
      <h1 className="serif">Still to confirm</h1>
      {note ? <p className="banner">{note}</p> : null}
      {trip?.reviewItems.map((item) => (
        <article key={item.id} className="card">
          <p>{item.message}</p>
          <p className="muted">{humanStatus(item.status)}{item.dismissed ? " · set aside" : ""}{item.resolution ? ` · ${item.resolution}` : ""}</p>
          {role === "owner" && !item.dismissed ? (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                const form = e.currentTarget;
                const resolution = String(new FormData(form).get("resolution") ?? "").trim();
                void save({
                  ...trip,
                  reviewItems: trip.reviewItems.map((row) => row.id === item.id ? {
                    ...row,
                    dismissed: true,
                    status: "resolved",
                    resolution: resolution || "Corrected by owner.",
                  } : row),
                }).then((result) => {
                  setNote(result.ok ? "Review item resolved." : result.message);
                });
              }}
            >
              <label className="field">Resolution
                <input name="resolution" placeholder="What was corrected" />
              </label>
              <button className="btn-secondary" type="submit">Resolve with this fact</button>
            </form>
          ) : null}
        </article>
      ))}
      {trip?.ideas.map((idea) => (
        <article key={idea.id} className="card">
          <p>{idea.title}</p>
          <p className="muted">Idea only, not a booking.</p>
        </article>
      ))}
    </main>
  );
}

export function PrintPage() {
  const { trip } = useTrip();
  const now = useTripClock();
  const [includeMoney, setIncludeMoney] = useState(false);
  if (!trip) return null;
  const allergyNotes = allergyLines(trip);
  const milk = allergyPhrasesForTrip(trip.thaiPhrases)[0];
  const phase = tripPhase(now, trip.trip.startDate, trip.trip.endDate, trip.trip.homeTimezone);
  const date = todayInZone(now, phase === "before" ? trip.trip.homeTimezone : trip.trip.diaryTimezone);
  const stay = stayForDate(trip, date) ?? trip.stays.find((s) => s.startDate >= date);
  const place = stay ? trip.places.find((p) => p.id === stay.placeId) : undefined;
  const pickup = trip.events.find((e) => e.kind === "transfer" && !e.archived && e.pickupInstruction && e.date >= date);
  const flights = trip.flights.filter((f) => f.departure.local.slice(0, 10) >= date).slice(0, 4);
  const generated = formatLongDate(now.toISOString().slice(0, 10));

  return (
    <main className="page print-sheet">
      <div className="print-controls">
        <Link className="btn-ghost" to="/essentials/emergency">Back</Link>
        <button className="btn" type="button" onClick={() => window.print()}>Print</button>
        <label className="field">
          <span>
            <input type="checkbox" checked={includeMoney} onChange={(e) => setIncludeMoney(e.target.checked)} />
            Include money and confirmation references
          </span>
        </label>
      </div>
      <h1 className="serif">Emergency summary</h1>
      <p>Generated {generated}. Passwords are never printed.</p>
      {milk ? (
        <>
          <h2 className="serif">Milk allergy</h2>
          {!milk.translationReviewed ? <p>Phrase aid only. Thai not owner-reviewed yet.</p> : <p>Reviewed wording.</p>}
          <p lang="th">{milk.thai}</p>
          <p>{milk.english}</p>
        </>
      ) : null}
      <h2 className="serif">Hotel</h2>
      <p>{place?.name} {place?.address}</p>
      {trip.trip.insuranceNote ? (
        <>
          <h2 className="serif">Insurance</h2>
          <p>{trip.trip.insuranceNote}</p>
        </>
      ) : null}
      {pickup ? (
        <>
          <h2 className="serif">Pickup</h2>
          <p>{pickup.pickupInstruction}</p>
        </>
      ) : null}
      <h2 className="serif">Flights</h2>
      {flights.map((f) => (
        <p key={f.id}>{f.number} {f.departure.airport} {f.departure.local.slice(0, 16).replace("T", " ")} → {f.arrival.airport}</p>
      ))}
      <h2 className="serif">Contacts</h2>
      {trip.contacts.length === 0 ? <p>No numbers recorded.</p> : [...trip.contacts].sort((a, b) => emergencyRank(a.role) - emergencyRank(b.role)).map((c) => (
        <p key={c.id}>{c.name} · {c.role} · {c.phone ?? "phone not recorded"}</p>
      ))}
      <h2 className="serif">Booking allergy notes</h2>
      {allergyNotes.length ? allergyNotes.map((line) => <p key={line}>{line}</p>) : <p>None recorded.</p>}
      {includeMoney ? trip.bookings.filter((b) => b.confirmation).map((b) => (
        <p key={b.id}>{b.title ?? b.roomName} · {b.confirmation}</p>
      )) : <p className="muted">Money and confirmation references omitted.</p>}
    </main>
  );
}
